
import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Shared promise to dedupe profile fetch across hook instances
let sharedProfileFetchPromise = null;

const useAuth = (redirectTo = '/') => {
     const navigate = useNavigate();
     const BASE_URL = process.env.REACT_APP_BASE_URL;
     const [profile, setProfile] = useState(null);
     const [isLoadingProfile, setIsLoadingProfile] = useState(false);
     const hasInitialized = useRef(false);
     const isFetching = useRef(false); // Prevent concurrent fetches per instance

     const getToken = useCallback(() => {
          return sessionStorage.getItem('token');
     }, []);

     const isAuthenticated = useCallback(() => {
          return !!getToken();
     }, [getToken]);

     const getProfileFromSession = () => {
          const profileData = sessionStorage.getItem('userProfile');
          return profileData ? JSON.parse(profileData) : null;
     };

     const saveProfileToSession = (profileData) => {
          sessionStorage.setItem('userProfile', JSON.stringify(profileData));
          if (profileData.user_role) sessionStorage.setItem('user_role', profileData.user_role);
          if (profileData.user_code) sessionStorage.setItem('user_code', profileData.user_code);
          if (profileData.user_name) sessionStorage.setItem('user_name', profileData.user_name);
          if (profileData.isLeaveApplicable !== undefined) sessionStorage.setItem('isLeaveApplicable', profileData.isLeaveApplicable);
     };

     const getIsLeaveApplicable = useCallback(() => {
          const value = sessionStorage.getItem('isLeaveApplicable');
          return value === 'Y' || value === 'true';
     }, []);

     const fetchProfile = useCallback(async (forceRefresh = false) => {
          // If cached in session and not forcing refresh -> use it
          const cachedProfile = getProfileFromSession();
          if (cachedProfile && !forceRefresh) {
               setProfile(cachedProfile);
               return cachedProfile;
          }

          const token = getToken();
          if (!token) {
               console.log('No token found, cannot fetch profile');
               return null;
          }

          // If a shared fetch is already in progress and not forcing refresh, reuse it
          if (sharedProfileFetchPromise && !forceRefresh) {
               try {
                    const sharedResult = await sharedProfileFetchPromise;
                    if (sharedResult) {
                         setProfile(sharedResult);
                    }
                    return sharedResult;
               } catch (err) {
                    return null;
               }
          }

          // Prevent concurrent fetches per instance (optional)
          if (isFetching.current) {
               console.log('Instance fetch already in progress, skipping...');
               return profile;
          }

          // Start a new shared fetch promise
          sharedProfileFetchPromise = (async () => {
               try {
                    isFetching.current = true;
                    setIsLoadingProfile(true);
                    console.log('Fetching profile from API (shared)...');
                    const targetUrl = `${BASE_URL}/user_profile_new`;
                    console.log('fetchProfile -> targetUrl:', targetUrl);
                    console.log('fetchProfile -> token present:', !!token);

                    const response = await fetch(targetUrl, {
                         method: 'GET',
                         headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`,
                         },
                    });

                    let result = null;
                    try {
                         result = await response.json();
                    } catch (e) {
                         // Non-JSON response
                         const text = await response.text();
                         console.warn('fetchProfile: non-JSON response:', text);
                         throw new Error('Non-JSON response from profile endpoint');
                    }

                    console.log('fetchProfile -> status:', response.status, 'body:', result);

                    if (response.ok && result && result.data) {
                         saveProfileToSession(result.data);
                         // Also mirror to localStorage temporarily for debugging
                         try { localStorage.setItem('userProfile', JSON.stringify(result.data)); } catch (e) { }
                         return result.data;
                    } else {
                         throw new Error(result.message || result.error || 'Failed to fetch profile');
                    }
               } finally {
                    // cleanup of flags happens in outer finally
               }
          })();

          try {
               const data = await sharedProfileFetchPromise;
               setProfile(data);
               return data;
          } catch (error) {
               console.error('Error fetching profile (shared):', error);
               return null;
          } finally {
               // Reset shared promise and instance flags
               sharedProfileFetchPromise = null;
               setIsLoadingProfile(false);
               isFetching.current = false;
          }
     }, [getToken, BASE_URL]);

     const clearProfile = useCallback(() => {
          sessionStorage.removeItem('userProfile');
          sessionStorage.removeItem('user_role');
          sessionStorage.removeItem('user_code');
          sessionStorage.removeItem('user_name');
          sessionStorage.removeItem('isLeaveApplicable');
          setProfile(null);
     }, []);

     const authFetch = useCallback(async (url, options = {}) => {
          const token = getToken();
          if (!token) throw new Error('No authentication token found');
          const response = await fetch(url, {
               ...options,
               headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    ...options.headers,
               },
          });
          return response;
     }, [getToken]);

     const logout = useCallback(() => {
          sessionStorage.removeItem('token');
          localStorage.removeItem('token');
          sessionStorage.clear();
          localStorage.clear();
          clearProfile();
          navigate(redirectTo);
     }, [navigate, redirectTo, clearProfile]);

     useEffect(() => {
          if (hasInitialized.current) {
               return;
          }
          hasInitialized.current = true;

          const initProfile = async () => {
               if (!isAuthenticated()) {
                    navigate(redirectTo);
                    return;
               }

               const cached = getProfileFromSession();
               if (cached) {
                    setProfile(cached);
               } else {
                    await fetchProfile(false);
               }
          };

          initProfile();
     }, []); // run once

     return {
          getToken,
          isAuthenticated,
          authFetch,
          profile,
          isLoadingProfile,
          fetchProfile,
          clearProfile,
          logout,
          getProfileFromSession,
          getIsLeaveApplicable,
     };
};

export default useAuth;
